import { AIContext, Message, type TokenUsage } from '@/apiTypes/AiTypes';
import { encodeB64 } from '@/utils/base64';
import { createSSEParser, SSEParserCallbacks } from '@/utils/sseParser';
import { getLocale } from '@/lib/locale';
import { withGroundcoverRumSessionHeaders } from '@/lib/groundcoverRumSession';
import { withApiLocale } from '@/lib/apiFetch';

export interface StreamChatOptions {
    message: Message;
    context?: AIContext;
    conversationId?: string | null;
    signal?: AbortSignal;
    baseUrl?: string;
    onTextDelta: (text: string) => void;
    onMetadata?: (metadata: { model: string; conversationId: string; usage: TokenUsage; finishReason: string }) => void;
    onError: (error: { message: string; code: string }) => void;
    onDone: (messageId: string) => void;
    onReplace?: (message: string) => void;
}

export const streamChat = async (options: StreamChatOptions): Promise<void> => {
    const { message, context, conversationId, signal, baseUrl = '/api', onTextDelta, onMetadata, onError, onDone, onReplace } = options;

    let response: Response;
    try {
        // The language goes in the URL too (withApiLocale): Front Door drops Accept-Language on caching routes.
        response = await fetch(withApiLocale(`${baseUrl}/ai/chat/stream`, getLocale()), {
            method: 'POST',
            // A raw fetch never passes through RTK Query's `prepareHeaders`, so the locale and the RUM
            // session header are set here. Without the locale, ZigBot answers in the browser's language
            // rather than the one picked in the switcher; without the session header, the chat's backend
            // spans can't be matched to the RUM session.
            headers: withGroundcoverRumSessionHeaders({
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Accept-Language': getLocale(),
            }),
            body: JSON.stringify({
                message: encodeB64(JSON.stringify(message)),
                ...(context && { context: encodeB64(JSON.stringify(context)) }),
                ...(conversationId && { conversationId }),
            }),
            signal,
        });
    }
    catch (error) {
        if (signal?.aborted || (error instanceof Error && error.name === 'AbortError')) {
            return;
        }
        onError({
            message: error instanceof Error ? error.message : 'Request failed',
            code: 'REQUEST_FAILED',
        });
        return;
    }

    if (!response.ok) {
        onError({
            message: `Request failed: ${response.status}`,
            code: 'REQUEST_FAILED',
        });
        return;
    }

    if (!response.body) {
        onError({
            message: 'No response body',
            code: 'NO_BODY',
        });
        return;
    }

    const callbacks: SSEParserCallbacks = {
        onTextDelta,
        onMetadata,
        onError,
        onDone,
        onReplace,
    };

    const parser = createSSEParser(callbacks);
    const reader = response.body.getReader();

    try {
        while (true) {
            const { done, value } = await reader.read();

            if (done) {
                parser.flush();
                // Note: onDone is called by the parser when it receives the done event with messageId.
                // A stream that closes without saying how it ended would otherwise leave the chat waiting
                // on a reply that never comes ("…" forever), so treat it as an error.
                if (!parser.ended) {
                    onError({ message: 'The response ended unexpectedly', code: 'STREAM_INCOMPLETE' });
                }
                break;
            }

            parser.feed(value);
        }
    }
    catch (error) {
        if (signal?.aborted || (error instanceof Error && error.name === 'AbortError')) {
            // Stream was cancelled by user
            return;
        }
        onError({
            message: error instanceof Error ? error.message : 'Stream failed',
            code: 'STREAM_ERROR',
        });
    }
};
